// @ts-nocheck

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, Trash2, Image } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface BackgroundImageUploadProps {
  eventId?: string;
  currentImageUrl?: string;
  onImageChange: (url: string) => void;
}

const BackgroundImageUpload: React.FC<BackgroundImageUploadProps> = ({
  eventId,
  currentImageUrl,
  onImageChange
}) => {
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be smaller than 10MB');
      return;
    }

    setUploading(true);

    try {
      // Generate unique filename
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop();
      const fileName = eventId 
        ? `${eventId}_${timestamp}.${fileExt}`
        : `temp_${timestamp}.${fileExt}`;

      // Delete existing file if there's one
      if (currentImageUrl && currentImageUrl.includes('event-backgrounds')) {
        const existingPath = currentImageUrl.split('/event-backgrounds/')[1];
        if (existingPath) {
          await supabase.storage
            .from('event-backgrounds')
            .remove([existingPath]);
        }
      }

      // Upload new file
      const { data, error } = await supabase.storage
        .from('event-backgrounds')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) throw error;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('event-backgrounds')
        .getPublicUrl(data.path);

      onImageChange(urlData.publicUrl);
      toast.success('Background image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload background image');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentImageUrl || !currentImageUrl.includes('event-backgrounds')) return;

    try {
      const path = currentImageUrl.split('/event-backgrounds/')[1];
      if (path) {
        const { error } = await supabase.storage
          .from('event-backgrounds')
          .remove([path]);

        if (error) throw error;
      }

      onImageChange('');
      toast.success('Background image deleted successfully');
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete background image');
    }
  };

  return (
    <div className="space-y-4">
      <Label htmlFor="background-image">Background Image</Label>
      <div className="text-sm text-gray-600 mb-2">
        Upload an optional background image for the event detail page. Recommended size: 1920x1080px or larger.
      </div>

      {currentImageUrl ? (
        <div className="relative border rounded-lg p-4 bg-gray-50">
          <img
            src={currentImageUrl}
            alt="Event Background"
            className="max-w-md max-h-48 object-cover rounded"
            onError={(e) => {
              console.error("Background image failed to load");
              e.currentTarget.style.display = 'none';
            }}
          />
          <div className="mt-2 flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => document.getElementById('background-image-input')?.click()}
              disabled={uploading}
            >
              <Image className="h-4 w-4 mr-2" />
              Replace Image
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDelete}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <Image className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <Button
            variant="outline"
            onClick={() => document.getElementById('background-image-input')?.click()}
            disabled={uploading}
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? 'Uploading...' : 'Upload Background Image'}
          </Button>
          <p className="text-sm text-gray-500 mt-2">
            PNG, JPG, GIF, WebP up to 10MB
          </p>
        </div>
      )}

      <Input
        id="background-image-input"
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileUpload}
      />
    </div>
  );
};

export default BackgroundImageUpload;